import { useState, useMemo } from "react";
import {
  PackagePlus,
  AlertCircle,
  Plus,
  ToggleLeft,
  ToggleRight,
  Search,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import { MarketingImageEditor } from "@/components/admin/images";
import {
  useBundles,
  useCreateBundle,
  useToggleBundle,
} from "@/hooks/admin/useMarketing";
import type { Bundle, CreateBundleInput } from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Create bundle modal
// ---------------------------------------------------------------------------

function CreateBundleModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const create = useCreateBundle();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceFcfa, setPriceFcfa] = useState("");
  const [productIds, setProductIds] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  // Placeholder for original price estimation
  const productCount = productIds
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean).length;

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }
    const price = parseFloat(priceFcfa);
    if (isNaN(price) || price <= 0) {
      toast.error("Le prix doit être un montant positif en FCFA.");
      return;
    }

    const ids = productIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length < 2) {
      toast.error("Un bundle doit contenir au moins 2 produits.");
      return;
    }

    const input: CreateBundleInput = {
      name,
      description,
      price_fcfa: price,
      product_ids: ids,
    };
    create.mutate(input, {
      onSuccess: () => {
        toast.success("Bundle cree.");
        onClose();
      },
      onError: () => toast.error("Erreur lors de la creation du bundle."),
    });
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouveau bundle"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex : Pack Laitier Famille"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Description courte du bundle"
            className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Prix du bundle (FCFA) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              value={priceFcfa}
              onChange={(e) => setPriceFcfa(e.target.value)}
              min={0}
              placeholder="ex : 8 500"
              className="w-full rounded-lg border border-stone-200 px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-stone-400">
              FCFA
            </span>
          </div>
          <p className="text-xs text-stone-400">
            Le prix du bundle doit être inférieur au total des produits
            individuels.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            IDs produits <span className="text-red-500">*</span>
          </label>
          <textarea
            value={productIds}
            onChange={(e) => setProductIds(e.target.value)}
            rows={3}
            placeholder="uuid-1, uuid-2, uuid-3"
            className="w-full resize-none rounded-lg border border-stone-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
          <p className="text-xs text-stone-400">
            Separes par des virgules \u2014 minimum 2 produits.{" "}
            {productCount > 0 && (
              <span className="font-medium text-stone-600">
                {productCount} produit(s) ajoute(s)
              </span>
            )}
          </p>
        </div>

        {/* Bundle image */}
        <MarketingImageEditor
          preset="bundle"
          value={imageUrl}
          onChange={(url) => setImageUrl(url)}
          label="Image du bundle (optionnel)"
        />

        {/* Validation hint */}
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
          <div className="text-xs text-amber-800">
            <p className="font-medium">Regle de prix</p>
            <p className="mt-0.5">
              Le serveur verifiera que le prix du bundle est inferieur a la
              somme des prix individuels des produits. Si ce n'est pas le cas, le
              bundle ne sera pas cree.
            </p>
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Savings badge
// ---------------------------------------------------------------------------

function SavingsBadge({
  savingsPercent,
}: {
  readonly savingsPercent: number;
}) {
  const color =
    savingsPercent >= 30
      ? "bg-emerald-100 text-emerald-800"
      : savingsPercent >= 15
      ? "bg-blue-100 text-blue-800"
      : "bg-stone-100 text-stone-700";

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${color}`}
    >
      -{savingsPercent} %
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Bundles() {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { data, isLoading, isError } = useBundles();
  const toggleBundle = useToggleBundle();

  const allBundles = data?.bundles ?? [];

  // Client-side search
  const bundles = useMemo(() => {
    if (!search.trim()) return allBundles;
    const q = search.toLowerCase();
    return allBundles.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.description.toLowerCase().includes(q),
    );
  }, [allBundles, search]);

  const activeCount = allBundles.filter((b) => b.is_active).length;
  const totalOrders = allBundles.reduce((acc, b) => acc + b.orders_count, 0);
  const totalSavings = allBundles.reduce(
    (acc, b) =>
      acc + (b.original_price_fcfa - b.price_fcfa) * b.orders_count,
    0,
  );
  const avgSavings =
    allBundles.length > 0
      ? Math.round(
          allBundles.reduce((acc, b) => acc + b.savings_percent, 0) /
            allBundles.length,
        )
      : 0;

  const handleToggle = (id: string) => {
    toggleBundle.mutate(id, {
      onSuccess: () => toast.success("Statut du bundle mis a jour."),
      onError: () => toast.error("Erreur lors du changement de statut."),
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <PackagePlus className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Lots / Bundles
              </h1>
              <p className="text-sm text-stone-500">
                Combinaisons de produits a prix reduit
              </p>
            </div>
          </div>
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nouveau bundle
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Actifs" value={activeCount} format="number" />
          <KPICard
            label="Commandes ce mois"
            value={totalOrders}
            format="number"
          />
          <KPICard
            label="Économies générées"
            value={totalSavings}
            format="currency"
          />
          <KPICard
            label="Économie moyenne"
            value={`${avgSavings} %`}
          />
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un bundle..."
            className="w-full rounded-lg border border-stone-200 bg-white pl-9 pr-3 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Table */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 rounded bg-stone-100" />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les bundles.
          </div>
        )}

        {!isLoading && !isError && bundles.length === 0 && (
          <EmptyState
            icon={<PackagePlus className="h-6 w-6" />}
            title="Aucun bundle"
            description={
              search
                ? `Aucun resultat pour "${search}".`
                : "Creez des lots de produits pour augmenter la valeur du panier."
            }
            action={
              search
                ? undefined
                : {
                    label: "Nouveau bundle",
                    onClick: () => setModalOpen(true),
                  }
            }
          />
        )}

        {!isLoading && !isError && bundles.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Nom
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Prix bundle
                    </th>
                    <th className="hidden md:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Prix original
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Économie
                    </th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Produits
                    </th>
                    <th className="hidden lg:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Commandes
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-stone-500">
                      Actif
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {bundles.map((b) => (
                    <tr
                      key={b.id}
                      className="hover:bg-stone-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-stone-900">{b.name}</p>
                        {b.description && (
                          <p className="text-xs text-stone-400 line-clamp-1 mt-0.5">
                            {b.description}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-stone-800">
                        {b.price_fcfa.toLocaleString("fr-SN")} FCFA
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-right tabular-nums text-stone-400 line-through">
                        {b.original_price_fcfa.toLocaleString("fr-SN")} FCFA
                      </td>
                      <td className="px-4 py-3 text-right">
                        <SavingsBadge savingsPercent={b.savings_percent} />
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {b.product_count}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                        {b.orders_count.toLocaleString("fr-SN")}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggle(b.id)}
                          className={`transition-colors ${
                            b.is_active
                              ? "text-emerald-600 hover:text-emerald-800"
                              : "text-stone-400 hover:text-stone-600"
                          }`}
                          title={b.is_active ? "Desactiver" : "Activer"}
                        >
                          {b.is_active ? (
                            <ToggleRight className="h-5 w-5" />
                          ) : (
                            <ToggleLeft className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <CreateBundleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </AdminLayout>
  );
}
